import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditableText } from '@/core/EditableText.atom';
import { FormulaEditorAsync } from '@/formula/FormulaEditorAsync.molecule';
import { Icon } from '@seeqdev/qomponents';

interface FormulaErrors {
  message: string;
  column: number;
  line: number;
}

interface FormulaWithParameters {
  formula: string;
  parameters: { name: string; identifier: string; item?: { id?: string } }[];
  formulaErrors?: FormulaErrors[];
}

export interface FormulaItemTableOption {
  key: string;
  display: string;
  allFormulas?: FormulaWithParameters[];
  id?: string;
  isNew?: boolean;
}

export interface FormulaItemTableIF {
  options: FormulaItemTableOption[];
  testId?: string;
  extraClassNames?: string;
  onEditClick?: (key: string) => void;
  onRemoveClick?: (key: string) => void;
  onAdditionalIconClick?: (key: string) => void;
  additionalIcon?: string;
  additionalIconTooltip?: string;
  onSecondaryIconClick?: (key: string) => void;
  secondaryIcon?: string;
  secondaryIconTooltip?: string;
  textEditable?: boolean;
  onTextChange?: (newValue: string, original: FormulaItemTableOption) => void;
  hasErrors?: boolean;
  newLabel?: string;
}

export const FormulaItemTable = ({
  extraClassNames,
  options,
  testId,
  onEditClick,
  onRemoveClick,
  onAdditionalIconClick,
  additionalIcon,
  additionalIconTooltip,
  onSecondaryIconClick,
  secondaryIcon,
  secondaryIconTooltip,
  onTextChange,
  textEditable,
  hasErrors = false,
  newLabel,
}: FormulaItemTableIF) => {
  const { t } = useTranslation();

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Expand all rows if hasErrors becomes true
  useEffect(() => {
    if (hasErrors) {
      setExpandedKeys(new Set(options.map((opt) => opt.key)));
    }
  }, [hasErrors, options]);

  const toggleRow = useCallback((option: FormulaItemTableOption) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(option.key)) {
        next.delete(option.key);
      } else {
        next.add(option.key);
      }
      return next;
    });
  }, []);

  const iconClassNames = 'tw:mt-[3px] tw:ml-[8px]';

  return (
    <table
      className={clsx('table table-striped table-condensed mb0 width-100percent', extraClassNames)}
      data-testid={testId}
      tabIndex={0}>
      <tbody>
        {options.map((option) => {
          const isOpen = expandedKeys.has(option.key);
          return (
            <tr key={option.key}>
              <td colSpan={1}>
                <div>
                  <div className="flexColumnContainer flexSpaceBetween gap5">
                    {!textEditable && (
                      <span className="flexGrowShrink">
                        <span className="text-bold">{t('NAME')}:</span> {option.display}
                        {option.isNew && newLabel && (
                          <span className="tw:ml-[7px] new-badge font-bold">{newLabel}</span>
                        )}
                      </span>
                    )}
                    {textEditable && onTextChange && (
                      <span className="flexGrowShrink tw:flex tw:flex-row tw:items-center tw:gap-1">
                        <span className="text-bold">{t('NAME')}:</span>
                        <EditableText
                          value={option.display}
                          onUpdate={(newText) => onTextChange(newText, option)}
                          textClasses="tw:inline-block"
                        />
                        {option.isNew && newLabel && (
                          <span className="tw:ml-[7px] new-badge font-bold">{newLabel}</span>
                        )}
                      </span>
                    )}
                    {option.allFormulas && (
                      <Icon
                        icon="fc fc-formula"
                        onClick={() => toggleRow(option)}
                        testId={`${testId}FormulaIcon-${option.key}`}
                        extraClassNames="tw:mt-[3px]"
                      />
                    )}
                    {onEditClick && (
                      <Icon
                        icon="fa-pencil"
                        onClick={() => onEditClick(option.key)}
                        testId={`${testId}EditIcon-${option.key}`}
                        extraClassNames={iconClassNames}
                      />
                    )}
                    {onAdditionalIconClick && additionalIcon && (
                      <Icon
                        icon={additionalIcon}
                        onClick={() => onAdditionalIconClick(option.key)}
                        testId={`${testId}ExtraIcon-${option.key}`}
                        extraClassNames={iconClassNames}
                        tooltip={additionalIconTooltip && t(additionalIconTooltip)}
                      />
                    )}
                    {onSecondaryIconClick && secondaryIcon && (
                      <Icon
                        icon={secondaryIcon}
                        onClick={() => onSecondaryIconClick(option.key)}
                        testId={`${testId}SecondaryExtraIcon-${option.key}`}
                        extraClassNames={iconClassNames}
                        tooltip={secondaryIconTooltip && t(secondaryIconTooltip)}
                      />
                    )}
                    {onRemoveClick && (
                      <Icon
                        icon="fa-trash"
                        onClick={() => onRemoveClick(option.key)}
                        testId={`${testId}RemoveIcon-${option.key}`}
                        extraClassNames={iconClassNames}
                      />
                    )}
                  </div>
                  {isOpen && option.allFormulas && (
                    <div>
                      {option.allFormulas.map((formula, idx) => (
                        <div
                          key={idx}
                          className={clsx(
                            idx === 0 && 'tw:mt-[5px]',
                            idx !== option.allFormulas!.length - 1 && 'tw:mb-[12px]',
                          )}>
                          <span className="text-bold">
                            {t(
                              option.allFormulas!.length > 1 ? 'FORMULA.FORMULA_COUNT' : 'FORMULA.FORMULA',
                              option.allFormulas!.length > 1 ? { count: idx + 1 } : undefined,
                            )}
                            :
                          </span>{' '}
                          <FormulaEditorAsync
                            formula={formula.formula}
                            readOnly={true}
                            readOnlyWithErrors={true}
                            showLineNumbers={false}
                            lineWrapping={true}
                            formulaErrors={formula.formulaErrors}
                          />
                          {formula.parameters && formula.parameters.length > 0 && (
                            <div>
                              <span className="text-bold tw:mt-[5px]">{t('FORMULA.DOCUMENTATION.PARAMETERS')}: </span>{' '}
                              {formula.parameters.map((param) => `${param.identifier} (${param.name})`).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
